// purchaseOrderItemFromProduct.js
//
// Create PURCHASE_ORDER_ITEM nodes from PRODUCT catalog records and relate them to a PO.

import { createNode as createNodeFromFactory } from 'utilities/NodeFactory';
import { buildHierarchyEdgeOptions } from 'utilities/edgeHierarchy';
import { stampExclusiveAncestry } from 'utilities/nodeAncestry';
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getResolvedFields } from 'utilities/NodeType';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { upsertEdge } from 'services/nodes/NodeEdgeService';
import { setNodeDocs } from 'services/nodes/NodeWriteService';
import { computePurchaseOrderItemLineTotal } from 'nodetypes/purchaseorder/view/purchaseOrderItemsModel';
import {
  PURCHASE_ORDER_ITEM_TYPE_KEY,
  PURCHASE_ORDER_TYPE_KEY,
} from 'nodetypes/purchaseorderitem/purchaseOrderItemKeys';

export const PRODUCT_TYPE_KEY = 'PRODUCT';

const COPY_FIELD_NAMES = Object.freeze([
  'sku',
  'manufacturer',
  'vendor',
  'unitPrice',
  'unit',
  'leadTime',
  'category',
]);

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * @param {unknown} value
 * @returns {string[]}
 */
function normalizeRelationKeys(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeString(entry)).filter(Boolean);
  }
  const single = normalizeString(value);
  return single ? [single] : [];
}

/**
 * @param {object|null|undefined} product
 * @param {string} fieldName
 * @returns {unknown}
 */
function readProductField(product, fieldName) {
  if (!product || !fieldName) return undefined;
  const fromHelper = readNodeFieldRawValue(
    product,
    { name: fieldName, source: 'product' },
    PRODUCT_TYPE_KEY,
  );
  if (fromHelper !== undefined) return fromHelper;

  const section = product?.data_product;
  if (section && typeof section === 'object' && fieldName in section) {
    return section[fieldName];
  }
  return undefined;
}

/**
 * Resolve which data_* section a PURCHASE_ORDER_ITEM field writes into.
 *
 * @param {object|null|undefined} itemType
 * @param {string} fieldName
 * @returns {string}
 */
function resolveItemSectionKey(itemType, fieldName) {
  const fields = Array.isArray(itemType?.schema?.fields)
    ? itemType.schema.fields
    : [];
  const resolved = getResolvedFields(PURCHASE_ORDER_ITEM_TYPE_KEY, { includeHidden: true }) || [];
  const all = [...fields, ...resolved];
  const fieldDef = all.find((field) => normalizeString(field?.name || field?.key) === fieldName);
  const source = normalizeString(
    fieldDef?.source || itemType?.key || PURCHASE_ORDER_ITEM_TYPE_KEY,
  ).toLowerCase();
  if (itemType?.dataSection && (!fieldDef?.source || fieldDef.source === itemType.key)) {
    return itemType.dataSection;
  }
  return source
    ? `data_${source}`
    : (itemType?.dataSection || 'data_purchase_order_item');
}

/**
 * Build a PURCHASE_ORDER_ITEM node payload from a PRODUCT (does not persist).
 *
 * @param {object} options
 * @param {object} options.product
 * @param {object} options.purchaseOrderNode
 * @param {object} options.user
 * @param {string|null} options.baseKey
 * @param {object|null|undefined} options.itemType
 * @returns {Promise<object>}
 */
export async function buildPurchaseOrderItemFromProduct({
  product,
  purchaseOrderNode,
  user,
  baseKey = null,
  itemType = null,
}) {
  const item = await createNodeFromFactory(PURCHASE_ORDER_ITEM_TYPE_KEY, user, baseKey || null);

  const productTitle = normalizeString(product?.title) || 'Untitled product';
  item.title = productTitle;

  const subtitle = normalizeString(product?.subtitle);
  if (subtitle) item.subtitle = subtitle;

  if (product?.coverUrl) item.coverUrl = product.coverUrl;
  if (product?.cover != null) item.cover = product.cover;

  item.parentTitle = normalizeString(purchaseOrderNode?.title);
  item.parentType = purchaseOrderNode?.type || PURCHASE_ORDER_TYPE_KEY;
  item.profileRoot = false;
  item.contributorKey = user?.key || null;

  const sectionWrites = {};

  const writeField = (fieldName, value) => {
    if (value === undefined) return;
    const sectionKey = resolveItemSectionKey(itemType, fieldName);
    const existing = sectionWrites[sectionKey] || (
      item[sectionKey] && typeof item[sectionKey] === 'object'
        ? { ...item[sectionKey] }
        : {}
    );
    existing[fieldName] = value;
    sectionWrites[sectionKey] = existing;
  };

  COPY_FIELD_NAMES.forEach((fieldName) => {
    const value = readProductField(product, fieldName);
    if (value === undefined || value === null || value === '') return;
    if (fieldName === 'vendor') {
      const keys = normalizeRelationKeys(value);
      if (!keys.length) return;
      writeField(fieldName, keys);
      return;
    }
    writeField(fieldName, value);
  });

  const productKey = normalizeString(product?.key);
  if (productKey) {
    writeField('product', [productKey]);
  }

  const quantity = 1;
  writeField('quantity', quantity);

  const unitPrice = readProductField(product, 'unitPrice');
  const total = computePurchaseOrderItemLineTotal(quantity, unitPrice);
  if (total != null) {
    writeField('total', total);
  }

  writeField('status', 'pending');

  Object.entries(sectionWrites).forEach(([sectionKey, values]) => {
    item[sectionKey] = values;
  });

  return stampExclusiveAncestry(
    item,
    purchaseOrderNode,
    itemType || getNodeType(PURCHASE_ORDER_ITEM_TYPE_KEY),
  );
}

/**
 * Create PURCHASE_ORDER_ITEM nodes from products and edge them to the PO.
 *
 * @param {object} options
 * @param {object[]} options.products
 * @param {object} options.purchaseOrderNode
 * @param {object} options.user
 * @param {string|null} [options.baseKey]
 * @param {object|null} [options.itemType]
 * @returns {Promise<object[]>} Created PO item nodes
 */
export async function createPurchaseOrderItemsFromProducts({
  products,
  purchaseOrderNode,
  user,
  baseKey = null,
  itemType = null,
}) {
  const list = (Array.isArray(products) ? products : []).filter((row) => row?.key);
  if (!list.length) return [];
  if (!purchaseOrderNode?.key || !user?.key) {
    throw new Error('Purchase order and user are required to create line items.');
  }

  const resolvedBaseKey = baseKey || purchaseOrderNode.baseKey || null;
  const entries = [];

  for (const product of list) {
    const item = await buildPurchaseOrderItemFromProduct({
      product,
      purchaseOrderNode,
      user,
      baseKey: resolvedBaseKey,
      itemType,
    });
    entries.push({ key: item.key, payload: item });
  }

  await setNodeDocs(entries, { merge: false });

  for (const entry of entries) {
    await upsertEdge(
      purchaseOrderNode.key,
      entry.key,
      buildHierarchyEdgeOptions({
        parentNode: purchaseOrderNode,
        childNode: entry.payload,
        baseKey: resolvedBaseKey,
      }),
    );
  }

  return entries.map((entry) => entry.payload);
}
